import React, { useEffect } from 'react';
import { OG_IMAGE_URL } from './config';
import { LanguageProvider, useLanguage } from './contexts/LanguageContext';
import { LeadProvider } from './contexts/LeadContext';
import { usePageTracking } from './hooks/usePageTracking';
import { Header } from './components/Header';
import { Hero } from './components/Hero';
import { PainPoints } from './components/PainPoints';
import { MeetTheQueens } from './components/MeetTheQueens';
import { QueenExperience } from './components/QueenExperience';
import { MostWanted } from './components/MostWanted';
import { TradeIn } from './components/TradeIn';
import { VideoSection } from './components/VideoSection';
import { LeadForm } from './components/LeadForm/LeadForm';
import { HIDE_LEAD_FORM_FOR_COMPLIANCE } from './config';
import { HowItWorks } from './components/HowItWorks';
import { Faq } from './components/Faq';
import { Footer } from './components/Footer';
import { SectionDivider } from './components/SectionDivider';
import { GHLWidget } from './components/GHLWidget';

/**
 * Head tags.
 *
 * robots: REMOVE BEFORE LAUNCH — placeholder content is currently live, so the page
 * must not be indexed yet.
 *
 * og:image: rendered only once OG_IMAGE_URL holds a real approved asset. Emitting a tag
 * that points nowhere makes WhatsApp and Meta render a broken preview card.
 */
function useHeadMeta() {
  useEffect(() => {
    const tags: HTMLMetaElement[] = [];
    const add = (attr: 'name' | 'property', key: string, content: string) => {
      const meta = document.createElement('meta');
      meta.setAttribute(attr, key);
      meta.content = content;
      document.head.appendChild(meta);
      tags.push(meta);
    };

    // REMOVE BEFORE LAUNCH
    add('name', 'robots', 'noindex, nofollow');
    if (OG_IMAGE_URL) {
      add('property', 'og:image', OG_IMAGE_URL);
      add('name', 'twitter:image', OG_IMAGE_URL);
      add('name', 'twitter:card', 'summary_large_image');
    }
    return () => {
      tags.forEach(tag => document.head.removeChild(tag));
    };
  }, []);
}
function Landing() {
  const {
    t
  } = useLanguage();
  usePageTracking();
  useHeadMeta();
  return <div className="w-full overflow-x-hidden bg-white" data-id="element-0" data-mp="3uc9jfxd5xojt">
      <a href="#main" className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-[70] focus:bg-ink focus:px-4 focus:py-2 focus:text-sm focus:font-bold focus:text-white" data-id="element-1" data-mp="d57h17dyqql6">
        {t('nav.skip')}
      </a>
      <Header data-id="element-2" data-mp="3glnz048296jt" />
      <main id="main" data-id="element-3" data-mp="3gj1r23h9cp12">
        {/* One argument per screen, each ending in the next step:
            hook → who they are → why different → what to pick → your car →
            what happens next → give your data → objections handled. */}
        <VideoSection src="https://cdn.magicpatterns.com/uploads/d5VZi8boURW3RqnLtkGKAS/A_REAL_SUV_15_1920x796.mp4" fullBleed priority data-id="element-4" data-mp="3lk7ky4mnmic6" />
        <Hero data-id="element-5" data-mp="2sw20zk3fcxde" />
        <PainPoints data-id="element-6" data-mp="2swbpt4cqgyhi" />
        <MeetTheQueens data-id="element-7" data-mp="2sv0p5del46um" />
        <SectionDivider data-id="element-8" data-mp="2svjy44m9bkwi" />
        <QueenExperience data-id="element-9" data-mp="18drkjz9bdlfe" />
        <MostWanted data-id="element-10" data-mp="18dhqo0l5eu4f" />
        <TradeIn data-id="element-11" data-mp="18cznek02jfn0" />
        <HowItWorks data-id="element-12" data-mp="18cggrzfvcdoa" />
        <VideoSection src="https://cdn.magicpatterns.com/uploads/xyHVoiyZwgDdy7V9eYcTtp/my27-telli-ice-hero-desktop_moov.mp4" data-id="element-13" data-mp="18bydiiusgz6v" />
        {!HIDE_LEAD_FORM_FOR_COMPLIANCE && <LeadForm data-id="element-14" data-mp="yh6cf51zzu7c" />}
        <SectionDivider data-id="element-15" data-mp="ydijo84dnbto" />
        <Faq data-id="element-16" data-mp="13eexpeig0ect" />
      </main>
      <Footer data-id="element-17" data-mp="13dwpdjiia9oj" />
      <GHLWidget data-id="element-18" data-mp="13fg9xmb736w4" />
    </div>;
}
export function App() {
  return <LanguageProvider data-id="element-19" data-mp="1r3r6id9uykel">
      <LeadProvider data-id="element-20" data-mp="1r5kd5skh14ml">
        <Landing data-id="element-21" data-mp="1r5257yog51yu" />
      </LeadProvider>
    </LanguageProvider>;
}